import booleanContains from '@turf/boolean-contains'
import centroid from '@turf/centroid'
import type { FeatureCollection } from 'geojson'
import { omit } from 'lodash-es'
import type { Document } from 'mongodb'
import { useEffect, useMemo, useState } from 'react'
import { useListingsQuery } from '~/api'
import {
  aDrawnFeature,
  aFiltersFind,
  aFiltersSort,
  aHiddenListings,
  aMapCurrentCount,
  aMapRef,
  aMapViewState,
  aMapZoomNextListingLoad,
  useAtom,
  useAtomValue,
  useSetAtom,
} from '~/atoms'
import { useEmbed, useMapCardSizes } from '~/hooks'
import { Listing } from '~/models'
import { calculateIdealZoom } from '~/utils'

export type MapOutletContextData = {
  listings: Listing[]
  isError: boolean
  isLoading: boolean
  isFetching: boolean
  fetchNextPage: () => void
  hasNextPage: boolean
}

export function useMapProperties() {
  const mapRef = useAtomValue(aMapRef)
  const mapViewState = useAtomValue(aMapViewState)
  const filtersFind = useAtomValue(aFiltersFind)
  const setCurrentMapCount = useSetAtom(aMapCurrentCount)
  const drawnFeature = useAtomValue(aDrawnFeature)
  const hiddenListings = useAtomValue(aHiddenListings)
  const { embedFind } = useEmbed()
  const { list } = useMapCardSizes()
  const [mapZoomNextListingLoad, setMapZoomNextListingLoad] = useAtom(
    aMapZoomNextListingLoad
  )

  const {
    listings: properties,
    count: totalCount,
    // isError,
    isLoading: isLoadingProperties,
    isFetching: isFetchingProperties,
  } = useListingsQuery({
    key: [
      'map-properties',
      'disable-cache',
      JSON.stringify(filtersFind),
      // hiddenListings.toString(),
    ],
    find: {
      ...filtersFind,
      ...embedFind,
      _id: { $nin: hiddenListings },
    },
    project: {
      slug: 1,
      name: 1,
      // 'property.address': 1,
      // 'property.address2': 1,
      property: 1,
      location: 1,
      type: 1,
      status: 1,
      propertyTypes: 1,
      isBusinessListing: 1,
      locationVisibility: 1,
    },
  })
  const drawnProperties = useMemo(() => {
    if (drawnFeature) {
      return properties.filter((l: any) =>
        booleanContains(drawnFeature, l.location)
      )
    }
    return properties
  }, [properties, drawnFeature])

  // useEffect(() => {
  //   if (drawnFeature) {
  //     setCheckedListings(drawnProperties.map(({ _id }) => _id))
  //   }
  // }, [drawnProperties, drawnFeature, setCheckedListings])

  useEffect(() => {
    if (mapViewState) {
      setTimeout(() => {
        if (!isLoadingProperties)
          setCurrentMapCount(
            drawnProperties.filter(
              (p) =>
                !hiddenListings.includes(p._id) &&
                mapViewState!.bounds.contains(
                  p.location.coordinates as [number, number]
                )
            ).length
          )
      }, 200)
    }
  }, [mapViewState, drawnProperties, isLoadingProperties, setCurrentMapCount])

  useEffect(() => {
    if (!mapRef) {
      return
    }
    if (!isLoadingProperties) {
      const bounds = mapRef.getBounds()
      const visibleProperties = drawnProperties.filter(
        (p) =>
          !hiddenListings.includes(p._id) &&
          bounds.contains(p.location.coordinates as [number, number])
      ).length
      if (
        drawnProperties.length > 0 &&
        (visibleProperties === 0 || mapZoomNextListingLoad)
      ) {
        if (mapZoomNextListingLoad) {
          setMapZoomNextListingLoad(false)
        }
        const geo: FeatureCollection = {
          type: 'FeatureCollection',
          features: drawnProperties.map((l: any) => {
            return {
              type: 'Feature',
              geometry: {
                ...l.location,
                type: 'Point',
              },
              properties: {},
            }
          }),
        }
        const cent = centroid(geo)
        const zoom = calculateIdealZoom(mapRef, geo.features)
        mapRef.easeTo({
          center: cent.geometry.coordinates as [number, number],
          padding: { left: list, top: 0, right: 0, bottom: 0 },
          zoom: zoom > 16 ? 16 : zoom,
        })
      }
    }
  }, [isLoadingProperties])

  return {
    properties: drawnProperties.filter((p) => !hiddenListings.includes(p._id)),
    totalCount,
    isLoadingProperties,
    isFetchingProperties,
  }
}

export function useMapListings({
  limit = 60,
  offset,
  project,
}: {
  limit?: number
  offset?: number
  project?: Document
}) {
  const mapViewState = useAtomValue(aMapViewState)
  const filtersFind = useAtomValue(aFiltersFind)
  const filtersSort = useAtomValue(aFiltersSort)
  const drawnFeature = useAtomValue(aDrawnFeature)
  const hiddenListings = useAtomValue(aHiddenListings)
  const [queryEnabled, setQueryEnabled] = useState(false)
  const { embedFind } = useEmbed()

  useEffect(() => {
    if (mapViewState) {
      setTimeout(() => {
        setQueryEnabled(true)
      }, 100)
    }
  }, [mapViewState])

  const { listingsFind, listingFindKey } = useMemo(() => {
    if (!mapViewState) {
      return {}
    }
    const find: { [key: string]: any } = {
      ...filtersFind,
      ...embedFind,
      _id: { $nin: hiddenListings },
    }

    if (drawnFeature) {
      find.location = {
        $geoWithin: {
          $geometry: drawnFeature.geometry,
        },
      }
    } else {
      const sw = Object.values(mapViewState.bounds.getSouthWest())
      const ne = Object.values(mapViewState.bounds.getNorthEast())
      find.location = {
        $geoWithin: {
          $geometry: {
            type: 'Polygon',
            coordinates: [
              [
                [sw[0], ne[1]],
                [ne[0], ne[1]],
                [ne[0], sw[1]],
                [sw[0], sw[1]],
                [sw[0], ne[1]],
              ],
            ],
          },
        },
      }
    }

    return { listingsFind: find, listingFindKey: omit(find, ['_id']) }
  }, [mapViewState?.bounds, filtersFind, drawnFeature, hiddenListings])

  const {
    listings,
    isError,
    isLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    count,
  } = useListingsQuery({
    key: [
      'map-listings',
      JSON.stringify({ ...listingFindKey, ...filtersSort }),
      ...(offset === undefined ? [] : [offset.toString()]),
    ],
    find: { status: 'active', ...listingsFind },
    sort: filtersSort,
    project: project ?? {
      location: 1,
      created: 1,
      modified: 1,
      coreModified: 1,
      name: 1,
      slug: 1,
      property: 1,
      type: 1,
      status: 1,
      propertyTypes: 1,
      filters: 1,
      images: { $slice: 1 },
      company: 1,
      brokers: 1,
      isBusinessListing: 1,
      locationVisibility: 1,
      'meta.totalUnits': 1,
    },
    limit,
    offset,
    enabled: queryEnabled,
  })

  return {
    listings: listings.filter((l) => hiddenListings.includes(l._id) === false),
    isError,
    isLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    count,
  }
}
